/**
 * ScreeningFiltersSection - embeds the technical screening filter categories
 * (MA Criteria, Volume & Momentum, Patterns, Advanced, Extended) inside the
 * Finviz Screener Dialog.  Reuses every component from ScreeningFiltersDialog
 * without modifying them.
 */
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import {
  BarChart2,
  Volume2,
  CandlestickChart,
  Target,
  Zap,
  DollarSign,
} from 'lucide-react';

// Reuse existing filter infrastructure (import, don't copy)
import {
  marketCriteriaFilters,
  maCriteriaFilters,
  volumeMomentumFilters,
  patternFilters,
  advancedFilters,
  extendedFilters,
  filterPillIdToCategoryId,
} from '../ScreeningFiltersDialog/filterConfig';
import { useFilterState } from '../ScreeningFiltersDialog/useFilterState';
import type {
  FilterValues,
  FilterCategoryItem,
  FilterItem,
} from '../ScreeningFiltersDialog/types';
import FilterSidebar from '../ScreeningFiltersDialog/FilterSidebar';
import FilterPillsBar from '../ScreeningFiltersDialog/FilterPillsBar';
import FilterContent from '../ScreeningFiltersDialog/FilterContent';
import { tCategories, tPills } from '../ScreeningFiltersDialog/filterI18n';

// ---------------------------------------------------------------------------
// Screening-only categories (excludes 'market' and 'finviz' - Finviz replaces
// the stock-universe selection that the market category handled)
// ---------------------------------------------------------------------------
const screeningCategories: FilterCategoryItem[] = [
  {
    id: 'market',
    label: 'Market & Targets',
    icon: DollarSign,
    description: 'Market cap, price targets, and index filters',
    filterIds: marketCriteriaFilters.map((f) => f.id),
  },
  {
    id: 'ma_criteria',
    label: 'MA Criteria',
    icon: BarChart2,
    description: 'Moving average proximity and timeframe',
    filterIds: maCriteriaFilters.map((f) => f.id),
  },
  {
    id: 'volume',
    label: 'Volume & Momentum',
    icon: Volume2,
    description: 'Volume surge threshold, high volume filter',
    filterIds: volumeMomentumFilters.map((f) => f.id),
  },
  {
    id: 'patterns',
    label: 'Patterns',
    icon: CandlestickChart,
    description: 'Chart patterns to identify trading opportunities',
    filterIds: patternFilters.map((f) => f.id),
  },
  {
    id: 'advanced',
    label: 'Advanced',
    icon: Target,
    description: 'Golden cross, Fibonacci, support',
    filterIds: advancedFilters.map((f) => f.id),
  },
  {
    id: 'extended',
    label: 'Extended',
    icon: Zap,
    description: 'Advanced technical filters',
    filterIds: extendedFilters.map((f) => f.id),
  },
];

// All screening filter items (flat list)
const allScreeningFilters: FilterItem[] = [
  ...marketCriteriaFilters,
  ...maCriteriaFilters,
  ...volumeMomentumFilters,
  ...patternFilters,
  ...advancedFilters,
  ...extendedFilters,
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface ScreeningFiltersSectionProps {
  initialValues: FilterValues;
  onValuesChange: (values: FilterValues) => void;
  className?: string;
}

/** Imperative handle exposed to parents (FinvizScreenerDialog) so the
 *  outer Reset button can clear screening fields back to catalog defaults. */
export interface ScreeningFiltersSectionHandle {
  resetToDefaults: () => void;
}

const ScreeningFiltersSection = forwardRef<
  ScreeningFiltersSectionHandle,
  ScreeningFiltersSectionProps
>(({ initialValues, onValuesChange, className }, ref) => {
  const { t } = useTranslation();
  const localisedCategories = useMemo(
    () => tCategories(screeningCategories, t),
    [t]
  );
  const {
    filterValues,
    activeCategory,
    activePills,
    expandedDetails,
    setValue: rawSetValue,
    setActiveCategory,
    resetFilter,
    resetToDefaults,
    toggleDetail,
    setDetailExpanded,
  } = useFilterState({ initialValues });

  // Notify parent whenever filterValues changes (covers both setValue and reset)
  React.useEffect(() => {
    onValuesChange(filterValues);
  }, [filterValues, onValuesChange]);

  // Use rawSetValue directly - the useEffect above handles parent notification
  const setValue = rawSetValue;

  // Reset to factory defaults
  const handleResetToDefaults = useCallback(() => {
    resetToDefaults();
    // useEffect above will notify parent with the reset values
  }, [resetToDefaults]);

  // Expose reset to parent so the outer dialog's Reset button can clear
  // every screening field - not just the universe (fs_*) inputs.
  useImperativeHandle(
    ref,
    () => ({
      resetToDefaults: handleResetToDefaults,
    }),
    [handleResetToDefaults]
  );

  // Default to first screening category if current active isn't in our list
  const effectiveCategory = screeningCategories.some(
    (c) => c.id === activeCategory
  )
    ? activeCategory
    : (screeningCategories[0]?.id ?? 'ma_criteria');

  // Get filters for the active category
  const currentCategoryFilters = useMemo(() => {
    const cat = screeningCategories.find((c) => c.id === effectiveCategory);
    if (!cat) return [];
    return allScreeningFilters.filter(
      (f) => cat.filterIds.includes(f.id) && !f.uiHidden
    );
  }, [effectiveCategory]);

  // Active pill counts per category (for sidebar badges)
  const activeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    screeningCategories.forEach((cat) => (counts[cat.id] = 0));
    activePills.forEach((pill) => {
      const catId = filterPillIdToCategoryId.get(pill.id);
      if (catId && counts[catId] !== undefined) {
        counts[catId]++;
      }
    });
    return counts;
  }, [activePills]);

  // Category-scoped pills - use effectiveCategory (not activeCategory) to stay
  // in sync with the rendered filter list in the same render frame.
  // Translated through `tPills` so chip labels match the active locale.
  const categoryPills = useMemo(
    () =>
      tPills(
        activePills.filter((pill) => {
          const catId = filterPillIdToCategoryId.get(pill.id);
          return catId === effectiveCategory;
        }),
        t
      ),
    [activePills, effectiveCategory, t]
  );

  const totalActive = activePills.length;
  const activeCategoryLabel =
    localisedCategories.find((c) => c.id === effectiveCategory)?.label ?? null;

  // Sync if category needs adjusting
  React.useEffect(() => {
    if (effectiveCategory !== activeCategory) {
      setActiveCategory(effectiveCategory);
    }
  }, [effectiveCategory, activeCategory, setActiveCategory]);

  return (
    <div
      className={cn('flex flex-col w-full h-full min-h-0 min-w-0', className)}
    >
      {/* Active filter pills */}
      {totalActive > 0 && (
        <FilterPillsBar
          className="max-lg:hidden"
          pills={categoryPills}
          onRemovePill={resetFilter}
          onClearAll={handleResetToDefaults}
          totalActiveAcrossCategories={totalActive}
          activeScopeCategoryLabel={activeCategoryLabel}
        />
      )}

      {/* Mobile category bar - outside the flex row so it stacks vertically */}
      <div className="flex lg:hidden shrink-0 w-full min-w-0 flex-nowrap gap-1 overflow-x-auto border-b border-border-subtle bg-surface-container-low px-2 py-1.5">
        {localisedCategories.map((cat) => {
          const isActive = effectiveCategory === cat.id;
          const Icon = cat.icon;
          const count = activeCounts[cat.id] || 0;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1.5 text-left text-xs font-medium leading-tight transition-colors min-h-[36px] sm:gap-1.5 sm:px-3 sm:py-2 sm:text-xs',
                isActive
                  ? 'bg-accent-primary/10 text-accent-primary'
                  : 'text-text-secondary hover:bg-surface-container'
              )}
            >
              <Icon className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" />
              <span className="min-w-0 truncate">{cat.label}</span>
              {count > 0 && (
                <span className="ml-1 text-xs bg-accent-primary/20 text-accent-primary px-1 rounded-full">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Sidebar + Content */}
      <div className="flex flex-1 min-h-0 min-w-0 w-full overflow-hidden">
        {/* Desktop sidebar */}
        <FilterSidebar
          categories={localisedCategories}
          activeCategory={effectiveCategory}
          onCategoryChange={setActiveCategory}
          activeCounts={activeCounts}
          className="hidden lg:flex"
        />

        {/* Filter content */}
        <div className="flex-1 overflow-hidden min-h-0 min-w-0 flex flex-col">
          <FilterContent
            filters={currentCategoryFilters}
            values={filterValues}
            onChange={setValue}
            expandedDetails={expandedDetails}
            onToggleDetail={toggleDetail}
            onSetDetailExpanded={setDetailExpanded}
            searchMode={false}
            className="flex-1 min-h-0 min-w-0"
          />
        </div>
      </div>
    </div>
  );
});

ScreeningFiltersSection.displayName = 'ScreeningFiltersSection';

export default ScreeningFiltersSection;
